/**
 * EmailDetailModal — open a saved email Attachment (kind='email') in a
 * full reader view. Mirrors the EmailPreviewModal layout but reads from a
 * stored Attachment (already-parsed fields + nested attachment URLs) rather
 * than a freshly-parsed File.
 */

import { getLocaleSync } from '@/utils/locale';
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Mail, Paperclip, Download, FileText, ExternalLink } from 'lucide-react';
import type { Attachment } from '@/types/attachment.types';
import { formatFileSize } from '@/services/attachment.service';

interface Props {
  attachment: Attachment;
  onClose: () => void;
}

export function EmailDetailModal({ attachment, onClose }: Props) {
  const hasHtml = !!attachment.emailHtmlBody;
  const hasText = !!attachment.emailBody;
  const [bodyMode, setBodyMode] = useState<'html' | 'text'>(
    hasHtml ? 'html' : 'text',
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const dateLabel = useMemo(() => {
    const iso = attachment.emailDate || attachment.timestamp;
    try {
      return new Date(iso).toLocaleString(getLocaleSync(), {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  }, [attachment.emailDate, attachment.timestamp]);

  const direction = attachment.emailDirection === 'outbound' ? 'Outbound' : 'Inbound';
  const subject = attachment.emailSubject || '(no subject)';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[400] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, y: 8 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96, y: 8 }}
        className="bg-surface-card border border-theme rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-3.5 border-b border-theme">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full bg-brand-secondary/15 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-brand-secondary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-theme-primary">
                {direction} email
              </h2>
              {attachment.addedByName && (
                <p className="text-[11px] text-theme-muted truncate">
                  Attached by {attachment.addedByName}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {attachment.url && (
              <a
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                title="Download original .eml"
                className="p-1.5 rounded-lg text-theme-muted hover:bg-surface-hover hover:text-theme-primary"
              >
                <Download className="w-4 h-4" />
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-theme-muted hover:bg-surface-hover hover:text-theme-primary"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Email headers */}
        <div className="px-5 py-3.5 border-b border-theme-soft space-y-1.5 text-xs">
          {attachment.emailFrom && (
            <HeaderRow label="From" value={attachment.emailFrom} />
          )}
          {attachment.emailTo && (
            <HeaderRow label="To" value={attachment.emailTo} />
          )}
          {attachment.emailCc && (
            <HeaderRow label="Cc" value={attachment.emailCc} />
          )}
          {attachment.emailBcc && (
            <HeaderRow label="Bcc" value={attachment.emailBcc} />
          )}
          <HeaderRow label="Date" value={dateLabel} muted />
          <div className="pt-1.5">
            <p className="text-base font-semibold text-theme-primary leading-snug">
              {subject}
            </p>
          </div>
        </div>

        {/* Body toggle */}
        {hasHtml && hasText && (
          <div className="px-5 pt-3 flex items-center gap-1.5">
            <BodyToggle active={bodyMode === 'html'} onClick={() => setBodyMode('html')}>
              Rich
            </BodyToggle>
            <BodyToggle active={bodyMode === 'text'} onClick={() => setBodyMode('text')}>
              Plain text
            </BodyToggle>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {bodyMode === 'html' && hasHtml ? (
            <EmailBodyIframe html={attachment.emailHtmlBody!} />
          ) : (
            <pre className="whitespace-pre-wrap break-words text-[13px] text-theme-primary font-sans leading-relaxed">
              {attachment.emailBody || '(empty body)'}
            </pre>
          )}
        </div>

        {/* Nested attachments */}
        {attachment.emailAttachments && attachment.emailAttachments.length > 0 && (
          <div className="px-5 py-3 border-t border-theme-soft">
            <div className="flex items-center gap-2 mb-2">
              <Paperclip className="w-3.5 h-3.5 text-theme-muted" />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-theme-muted">
                {attachment.emailAttachments.length} attachment
                {attachment.emailAttachments.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="space-y-1.5">
              {attachment.emailAttachments.map((att) => (
                <a
                  key={att.id}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 p-2.5 rounded-lg border border-theme bg-surface-hover hover:border-brand-secondary/50 transition-colors"
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-theme-primary truncate">
                      {att.fileName}
                    </p>
                    <p className="text-[11px] text-theme-muted">
                      {formatFileSize(att.fileSize)}
                    </p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-theme-muted group-hover:text-brand-secondary" />
                </a>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function HeaderRow({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <span className="text-theme-muted uppercase tracking-wide text-[10px] font-semibold w-10 flex-shrink-0 pt-0.5">
        {label}
      </span>
      <span
        className={`flex-1 break-words ${
          muted ? 'text-theme-muted' : 'text-theme-primary'
        }`}
      >
        {value || '—'}
      </span>
    </div>
  );
}

function BodyToggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
        active
          ? 'bg-brand-secondary/15 text-brand-secondary'
          : 'text-theme-muted hover:bg-surface-hover hover:text-theme-primary'
      }`}
    >
      {children}
    </button>
  );
}

function EmailBodyIframe({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(360);

  const wrapped = useMemo(() => {
    return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
      html, body { margin: 0; padding: 12px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; font-size: 13px; line-height: 1.55; color: #1a2238; background: transparent; word-break: break-word; }
      @media (prefers-color-scheme: dark) { html, body { color: #e2e8f0; } }
      img, video { max-width: 100%; height: auto; }
      table { max-width: 100%; }
      a { color: #2563eb; }
      blockquote { margin: 0 0 0 8px; padding-left: 10px; border-left: 2px solid rgba(127,127,127,0.3); color: rgba(127,127,127,0.95); }
      pre { white-space: pre-wrap; word-break: break-word; }
    </style></head><body>${html}</body></html>`;
  }, [html]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const updateHeight = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const h = Math.min(
          Math.max(doc.documentElement.scrollHeight, 120),
          520,
        );
        setHeight(h + 4);
      } catch {
        // ignore
      }
    };
    iframe.addEventListener('load', updateHeight);
    const t = setTimeout(updateHeight, 250);
    return () => {
      iframe.removeEventListener('load', updateHeight);
      clearTimeout(t);
    };
  }, [wrapped]);

  return (
    <iframe
      ref={iframeRef}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      srcDoc={wrapped}
      title="Email body"
      className="w-full border-0 bg-transparent"
      style={{ height: `${height}px` }}
    />
  );
}
